import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DeviceSnapshot } from '@/components/device/DeviceSnapshot';
import { usePolygonEditor } from '@/lib/geometry/usePolygonEditor';
import { DialogFooter } from '../ui/dialog';

interface GridEditorProps {
  deviceId: string;
  deviceName?: string;
  initialWkt?: string;
  onSave?: (polygons: string[]) => void;
  onCancel?: () => void;
  className?: string;
  showActions?: boolean;
  projectId?: string;
  companyId?: string;
  videoProcessorId?: string;
}

export const GridEditor: React.FC<GridEditorProps> = ({
  deviceId,
  initialWkt,
  onSave,
  onCancel,
  className = '',
  showActions = true,
  projectId,
  companyId,
  videoProcessorId,
}) => {
  const [hasLoadedImg, setHasLoadedImg] = useState(false);

  // Initialize polygon editor for grid editing
  const poly = usePolygonEditor({});

  // Load initial WKT only when component mounts (not on every initialWkt change)
  React.useEffect(() => {
    if (initialWkt) {
      const features = poly.util.fromWkt(initialWkt);
      const currentPolygons = poly.form.getValues('polygons') || [];
      poly.form.reset({ polygons: features });
    }
  }, [initialWkt]);

  const handleSave = () => {
    const polygons = poly.form.getValues('polygons');
    // Convert polygons back to WKT strings
    const wktStrings = polygons.map(polygon => poly.util.toWkt([polygon]));
    console.log('Saving polygons:', wktStrings);
    if (onSave) {
      onSave(wktStrings);
    }
  };

  const handleCancel = () => {
    poly.form.reset();
    if (onCancel) {
      onCancel();
    }
  };


  return (
    <div className={`space-y-4 ${className}`}>
      {/* Grid Editor Canvas */}
      <div className="relative h-max w-full" ref={poly.targetRef}>
        <DeviceSnapshot
          deviceId={deviceId}
          projectId={projectId}
          companyId={companyId}
          videoProcessorId={videoProcessorId}
          className="pointer-events-none w-full select-none"
          withLoadingText={false}
          onLoad={() => {
            poly.reload();
            setHasLoadedImg(true);
          }}
        />
        <canvas
          ref={poly.canvasRef}
          {...poly.canvasProps}
        />
      </div>
      
      {/* Status Indicator */}
      <div className="flex items-start justify-end px-4 pb-4 text-sm">
        <div className="flex items-center gap-x-2">
          <span className="text-white/70">
            {poly.form.getValues('polygons').length === 0
              ? 'Entire Frame Motion Detection'
              : 'Motion Detection Zone'}
          </span>
          {poly.form.getValues('polygons').length > 0 && (
            <div className="h-3 w-3 rounded border border-lime bg-lime/30" />
          )}
        </div>
      </div>
      
      {/* Action Buttons */}
      {showActions && (
        <DialogFooter className="">
          <Button
            variant="outline"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Grid
          </Button>
        </DialogFooter>
      )}
    </div>
  );
};